// CommentNode (ENG-1119) — a resizable box you can type in, behind the graph.
//
// It has no handles and no pins: it is documentation that travels with the
// graph, and both runtimes ignore it by construction (see lib/graphComment.js).
//
// Three details are load-bearing:
//
//   THE TEXTAREA IS `nodrag nowheel`. Without `nodrag`, selecting text drags the
//   node instead — xyflow's d3-drag binds a native mousedown and a React handler
//   cannot outrun it (see lib/nodeControlClass.js). Without `nowheel`, scrolling
//   a long comment zooms the canvas.
//
//   DELETE AND BACKSPACE ARE STOPPED while typing. The editors bind those to
//   "delete the selected node", so without this, erasing a character deletes the
//   comment you are writing in — with the text you just typed.
//
//   THE BOX ONLY CATCHES THE POINTER ON ITS CHROME. `pointer-events: none` on
//   the body with the header, border and textarea opting back in, so a comment
//   stretched across the canvas does not swallow clicks on the nodes inside it.
//   That is the difference between a backdrop and a lid.

import { memo, useState } from 'react'
import { NodeResizer } from '@xyflow/react'
import { nodeControlClass } from '../../lib/nodeControlClass.js'
import { MIN_COMMENT_SIZE, isCommentPendingEdit, releaseCommentEdit, DEFAULT_COMMENT_COLOR } from '../../lib/graphComment.js'
import { commentPalette } from './commentTheme.js'

const TEXT_CLASS = nodeControlClass({ scrollable: true })

function CommentNode({ id, data, selected }) {
  // A comment you just made opens IN text entry — Unreal's RequestRenameOnSpawn,
  // which its own comment node inherits. You only ever create one in order to
  // write in it, so requiring a double-click first is a second gesture for
  // nothing. The claim is consumed once (graphComment.js), so a later remount
  // cannot re-open the editor over someone's cursor.
  const [editing, setEditing] = useState(() => isCommentPendingEdit(id))
  const text = data?.text ?? ''
  // Per-comment colour (Unreal's CommentColor). Derived every render rather
  // than stored: one hex in the node, every surface tint computed from it, so a
  // new colour cannot half-apply.
  const C = commentPalette(data?.color ?? DEFAULT_COMMENT_COLOR, selected)

  return (
    <>
      <NodeResizer
        color={C.accent}
        isVisible={selected}
        minWidth={MIN_COMMENT_SIZE.width}
        minHeight={MIN_COMMENT_SIZE.height}
      />
      <div
        data-testid={`comment-${id}`}
        style={{
          width: '100%', height: '100%', boxSizing: 'border-box',
          background: C.fill,
          border: `2px solid ${C.border}`,
          borderRadius: 6,
          display: 'flex', flexDirection: 'column',
          // The body is a backdrop: clicks pass THROUGH to whatever it covers.
          pointerEvents: 'none',
        }}
      >
        {/* The header is the drag handle — deliberately the only large grabbable
            area, so dragging a comment is an explicit act rather than something
            that happens when you miss a node inside it. */}
        <div
          data-testid={`comment-header-${id}`}
          style={{
            flex: 'none', padding: '3px 8px', cursor: 'grab',
            borderBottom: `1px solid ${C.headerLine}`,
            color: C.text, fontSize: 10, letterSpacing: '.08em',
            textTransform: 'uppercase', fontFamily: 'var(--m-font)',
            pointerEvents: 'auto',
            background: C.header,
            borderRadius: '5px 5px 0 0',
          }}
          title="Drag to move the comment and everything inside it"
        >
          comment
        </div>

        {editing ? (
          <textarea
            className={TEXT_CLASS}
            autoFocus
            value={text}
            data-testid={`comment-text-${id}`}
            onChange={(e) => { releaseCommentEdit(id); data?.onChange?.('text', e.target.value) }}
            onBlur={() => { releaseCommentEdit(id); setEditing(false) }}
            onKeyDown={(e) => {
              // The editors delete the selected node on these; inside a textarea
              // they are editing keys and must not reach the canvas.
              if (e.key === 'Delete' || e.key === 'Backspace') e.stopPropagation()
              if (e.key === 'Escape') { e.preventDefault(); e.currentTarget.blur() }
            }}
            placeholder="Write a note…"
            style={{
              flex: 1, width: '100%', boxSizing: 'border-box', resize: 'none',
              background: 'transparent', border: 'none', outline: 'none',
              color: C.text, fontSize: 12, lineHeight: 1.45, padding: '6px 8px',
              fontFamily: 'var(--m-font)', pointerEvents: 'auto',
            }}
          />
        ) : (
          <div
            data-testid={`comment-body-${id}`}
            onDoubleClick={() => setEditing(true)}
            title="Double-click to edit"
            style={{
              flex: 1, padding: '6px 8px', color: C.text, fontSize: 12,
              lineHeight: 1.45, whiteSpace: 'pre-wrap', overflow: 'hidden',
              fontFamily: 'var(--m-font)', cursor: 'text',
              opacity: text ? 1 : 0.5,
              // Only the text area itself takes clicks, so the empty expanse of
              // a large comment still lets the nodes under it be used.
              pointerEvents: 'auto',
            }}
          >
            {text || 'Double-click to write a note…'}
          </div>
        )}
      </div>
    </>
  )
}

export default memo(CommentNode)
